import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft } from "lucide-react";
import { usePageSEO } from "../lib/seo";

export default function PrivacyPage() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  usePageSEO({ title: "Privacy Policy", description: "Stansbury AI Privacy Policy — how we collect, use, and protect your data. Compliant with PIPEDA, GDPR, and CCPA.", path: "/privacy" });

  const Section = ({ title, children }) => (
    <div className="mb-10">
      <h2 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mb-4">{title}</h2>
      <div className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed space-y-3">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="privacy-page">
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16">
          <button onClick={() => navigate("/")}><img src="/stansbury-logo.png" alt="Stansbury AI" className="h-[46px] sm:h-12 flex-shrink-0" /></button>
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-600 font-['Outfit'] text-sm"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">Legal</p>
        <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-12">Last updated: April 2026</p>

        <Section title="1. Introduction">
          <p>Stansbury AI ("we", "us", "our") is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services, including the Stansbury AI Growth Blueprint platform.</p>
          <p>This policy is designed to comply with the Canadian Personal Information Protection and Electronic Documents Act (PIPEDA), the European General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and other applicable global privacy laws.</p>
        </Section>

        <Section title="2. Information We Collect">
          <p><strong>Information you provide directly:</strong></p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Account information: name, email address, company name, role</li>
            <li>Audit responses: answers to our AI Growth Blueprint questionnaire</li>
            <li>Business information: website URL, business description, industry, company size, revenue range</li>
            <li>Booking information: name, email, phone number, scheduling preferences</li>
            <li>Payment information: processed securely through third-party payment processors</li>
            <li>Communications: messages, feedback, and support inquiries</li>
          </ul>
          <p><strong>Information collected automatically:</strong></p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Device and browser information</li>
            <li>IP address and approximate location</li>
            <li>Pages visited, time spent, and interaction patterns</li>
            <li>Cookies and similar tracking technologies (see Cookie Policy below)</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use collected information to:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Provide, maintain, and improve our AI Growth Blueprint services</li>
            <li>Generate personalized blueprint reports and recommendations</li>
            <li>Process transactions and send related information</li>
            <li>Send administrative communications about your account or our services</li>
            <li>Provide customer support and respond to inquiries</li>
            <li>Analyze usage patterns to improve user experience</li>
            <li>Protect against unauthorized access and ensure security</li>
            <li>Comply with legal obligations</li>
          </ul>
        </Section>

        <Section title="4. Legal Basis for Processing (GDPR)">
          <p>For users in the European Economic Area, we process personal data based on:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Consent:</strong> When you opt in to marketing communications or non-essential cookies</li>
            <li><strong>Contract:</strong> When processing is necessary to provide our blueprint services</li>
            <li><strong>Legitimate interest:</strong> For analytics, security, and service improvements</li>
            <li><strong>Legal obligation:</strong> When required to comply with applicable laws</li>
          </ul>
        </Section>

        <Section title="5. Cookie Policy">
          <p>We use cookies and similar technologies to enhance your experience:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Strictly Necessary Cookies:</strong> Required for the website to function properly, including authentication and security. These cannot be disabled.</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site, which pages are most popular, and how to improve our services.</li>
            <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertising and track the effectiveness of marketing campaigns.</li>
          </ul>
          <p>You can manage your cookie preferences at any time through the cookie consent banner or by adjusting your browser settings. Note that disabling certain cookies may affect site functionality.</p>
        </Section>

        <Section title="6. Data Sharing and Disclosure">
          <p>We do not sell your personal information. We may share data with:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Service providers:</strong> Third-party companies that help us operate our platform (hosting, payment processing, email delivery, analytics)</li>
            <li><strong>AI processing:</strong> Anonymized or pseudonymized data may be processed by AI services to generate blueprint reports</li>
            <li><strong>Legal requirements:</strong> When required by law, regulation, or legal process</li>
            <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          </ul>
          <p>All third-party service providers are contractually required to protect your data in accordance with applicable privacy laws.</p>
        </Section>

        <Section title="7. Data Retention">
          <p>We retain personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy. Specifically:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Account data: Retained while your account is active and for 2 years after closure</li>
            <li>Blueprint data and reports: Retained for 3 years from creation</li>
            <li>Booking records: Retained for 1 year after the scheduled date</li>
            <li>Analytics data: Aggregated and anonymized after 26 months</li>
          </ul>
          <p>You may request earlier deletion of your data (see Your Rights below).</p>
        </Section>

        <Section title="8. Data Security">
          <p>We implement industry-standard security measures to protect your personal information, including:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Encryption of data in transit (TLS/SSL) and at rest</li>
            <li>Secure authentication with password hashing (bcrypt)</li>
            <li>Access controls and role-based permissions</li>
            <li>Regular security monitoring and updates</li>
          </ul>
          <p>While we strive to protect your information, no method of transmission over the Internet is 100% secure.</p>
        </Section>

        <Section title="9. Your Rights">
          <p>Depending on your jurisdiction, you may have the following rights:</p>
          <div className="bg-white border border-slate-200 p-5 my-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["Access", "Request a copy of the personal data we hold about you"],
                ["Rectification", "Request correction of inaccurate personal data"],
                ["Erasure", "Request deletion of your personal data"],
                ["Restriction", "Request limitation of processing your data"],
                ["Portability", "Receive your data in a structured, machine-readable format"],
                ["Objection", "Object to processing based on legitimate interests"],
                ["Withdrawal", "Withdraw consent at any time (for consent-based processing)"],
                ["Non-discrimination", "Exercise your rights without discriminatory treatment (CCPA)"],
              ].map(([right, desc]) => (
                <div key={right} className="border-l-2 border-[#0033CC] pl-3">
                  <p className="text-xs font-['Outfit'] font-medium text-[#0F172A]">{right}</p>
                  <p className="text-[11px] text-slate-500">{desc}</p>
                </div>
              ))}
            </div>
          </div>
          <p>To exercise any of these rights, please contact us at the email address provided below.</p>
        </Section>

        <Section title="10. PIPEDA Compliance (Canada)">
          <p>In accordance with PIPEDA, we:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Obtain meaningful consent before collecting, using, or disclosing personal information</li>
            <li>Limit collection to purposes identified at or before the time of collection</li>
            <li>Retain information only as long as necessary for identified purposes</li>
            <li>Ensure accuracy of personal information</li>
            <li>Provide individuals with access to their personal information upon request</li>
            <li>Are accountable for personal information under our control</li>
          </ul>
        </Section>

        <Section title="11. International Data Transfers">
          <p>Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place for international transfers, including standard contractual clauses or other legally recognized mechanisms.</p>
        </Section>

        <Section title="12. Children's Privacy">
          <p>Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it promptly.</p>
        </Section>

        <Section title="13. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on this page with a revised "Last updated" date. Your continued use of our services after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="14. Contact Us">
          <p>If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:</p>
          <div className="bg-[#0F172A] p-6 mt-3">
            <p className="text-sm text-white font-['Outfit'] font-medium mb-3">Stansbury AI — Privacy Team</p>
            <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">Email: privacy@stansbury.ai</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-700">
              <div>
                <p className="text-xs text-slate-500 font-['JetBrains_Mono'] uppercase tracking-wider mb-1">United States</p>
                <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] leading-relaxed">Stansbury AI<br />2222 Ponce De Leon Blvd<br />Miami, FL 33134, USA</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-['JetBrains_Mono'] uppercase tracking-wider mb-1">Canada</p>
                <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] leading-relaxed">Stansbury AI<br />2967 Dundas St. W. #249D<br />Toronto, ON M6P 1Z2, Canada</p>
              </div>
            </div>
            <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mt-4">We will respond to all privacy-related inquiries within 30 days, as required by applicable privacy legislation.</p>
          </div>
        </Section>
      </div>

      <footer className="bg-slate-950 text-white py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row justify-between items-center gap-4">
          <img src="/stansbury-logo-white.png" alt="Stansbury AI" className="h-10" />
          <p className="text-xs text-slate-500 font-['IBM_Plex_Sans']"><a href="/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</a> · Confidential & Secure</p>
        </div>
      </footer>
    </div>
  );
}
